import { QwickApp, Logo, QwickIcon } from '@qwickapps/react-framework';
import { BrowserRouter, Route } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { AuthProvider, AuthRoutes, RouteGuard, SupabaseAuthProvider, useAuth } from '@qwickapps/auth-client';
import { useMiddlewareClient } from '@qwickforge/react-client';

import appConfig from './config/AppConfig';
import { config } from './config/runtime';

// Import pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import DeviceSetupPage from './pages/DeviceSetupPage';
import QuickStartPage from './pages/QuickStartPage';
import DashboardPage from './pages/DashboardPage';
import ProjectChatPage from './pages/ProjectChatPage';
import ProjectsPage from './pages/ProjectsPage';
import DevicesPage from './pages/DevicesPage';
import ProfilePage from './pages/ProfilePage';
import DownloadsPage from './pages/DownloadsPage';
import SDLCDashboardPage from './pages/SDLCDashboardPage';
import SDLCDashboardDemo from './pages/SDLCDashboardDemo';
import SessionsPage from './pages/SessionsPage';

// Create Supabase auth provider
const supabaseAuthProvider = new SupabaseAuthProvider({
  supabaseUrl: config.supabaseUrl,
  supabaseKey: config.supabaseAnonKey,
  features: {
    emailVerification: false,
    passwordReset: false,
    registration: false,
  },
  redirectUrls: {
    // Use full URL for OAuth redirects to work correctly across different domains
    afterSignIn: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '/auth/callback',
    afterSignOut: '/',
    afterSignUp: typeof window !== 'undefined' ? `${window.location.origin}/setup/device` : '/setup/device',
  },
  enabledProviders: ['github'], // Only GitHub is enabled in Supabase config
});

// Make auth provider accessible to LoginPage for enabled providers check
(window as any).__supabaseAuthProvider = supabaseAuthProvider;

const logo = (
  <Logo
    name="wick ForgeIDE"
    size="small"
    image={<QwickIcon size={32} style={{ position: 'relative', left: '25px', zIndex: -1 }} />}
    imagePosition="start"
    style={{ cursor: 'pointer' }}
    onClick={() => { window.location.href = '/' }}
  />
);

const menu = [
  { id: 'dashboard', label: 'Dashboard', route: '/dashboard', icon: 'dashboard' },
  { id: 'sdlc', label: 'Forge', route: '/sdlc', icon: 'monitor_heart' },
  { id: 'sessions', label: 'Sessions', route: '/sessions', icon: 'history' },
  { id: 'projects', label: 'Projects', route: '/projects', icon: 'business' },
  { id: 'devices', label: 'Devices', route: '/devices', icon: 'computer' },
  { id: 'downloads', label: 'Downloads', route: '/downloads', icon: 'download' },
  { id: 'profile', label: 'Profile', route: '/profile', icon: 'person' },
];

// Routes component that uses auth context
const AppRoutes: React.FC = () => {
  return (
    <AuthRoutes enableDefaultAuthRoutes={false}>
      {/* Public routes */}
      <Route key="landing" path="/" element={<LandingPage />} />
      <Route key="login" path="/login" element={<LoginPage />} />
      <Route key="auth-callback" path="/auth/callback" element={<AuthCallbackPage />} />
      <Route key="quick-start" path="/quick-start" element={<QuickStartPage />} />
      <Route key="downloads" path="/downloads" element={<DownloadsPage />} />
      
      {/* Demo route - no auth required */}
      <Route key="sdlc-demo" path="/sdlc-demo" element={<SDLCDashboardDemo />} />
      
      {/* Protected routes - require authentication */}
      <Route key="device-setup" path="/setup/device" element={<RouteGuard fallback={<LoginPage />}><DeviceSetupPage /></RouteGuard>} />
      <Route key="dashboard" path="/dashboard" element={<RouteGuard fallback={<LoginPage />}><DashboardPage /></RouteGuard>} />
      <Route key="projects" path="/projects" element={<RouteGuard fallback={<LoginPage />}><ProjectsPage /></RouteGuard>} />
      <Route key="project-chat" path="/projects/:id/chat" element={<RouteGuard fallback={<LoginPage />}><ProjectChatPage /></RouteGuard>} />
      <Route key="sessions" path="/sessions" element={<RouteGuard fallback={<LoginPage />}><SessionsPage /></RouteGuard>} />
      <Route key="devices" path="/devices" element={<RouteGuard fallback={<LoginPage />}><DevicesPage /></RouteGuard>} />
      <Route key="profile" path="/profile" element={<RouteGuard fallback={<LoginPage />}><ProfilePage /></RouteGuard>} />
      <Route key="sdlc-dashboard" path="/sdlc" element={<RouteGuard fallback={<LoginPage />}><SDLCDashboardPage /></RouteGuard>} />
    </AuthRoutes>
  );
};

// Content component that can access auth context
const AppContent: React.FC = () => {
  const [, setNavigationItems] = useState<any[]>(menu);
  const { user, session } = useAuth();
  const client = useMiddlewareClient();
  const [hasDevice, setHasDevice] = useState(false);

  useEffect(() => {
    const checkDevices = async () => {
      if (!user || !session?.accessToken || !client) {
        setHasDevice(false);
        return;
      }

      try {
        const data = await client.devices.list();
        const devices = Array.isArray(data) ? data : data.devices || [];
        setHasDevice(devices.length > 0);
      } catch (error) {
        console.error('Failed to check devices:', error);
        setHasDevice(false);
      }
    };

    checkDevices();
  }, [user?.id, session?.accessToken, client]);

  useEffect(() => {
    // Update navigation items based on auth and device status
    setNavigationItems(user && hasDevice ? menu : []);
  }, [user, hasDevice]);

  return <AppRoutes />;
};

const App: React.FC = () => {
  return (
    <QwickApp
      config={appConfig}
      logo={logo}
      navigationItems={menu}
      router={<BrowserRouter />}
    >
      <AuthProvider authServiceClient={supabaseAuthProvider}>
        <AppContent />
      </AuthProvider>
    </QwickApp>
  );
};

export default App;
